import Tooltip from '@/components/ui/tooltip';

import { hapticsImpactLight } from '../../../../utils/haptics';
import {
  ArrowRight,
  BookOpen,
  Calendar,
  PencilSimple,
  GitMerge,
  RadioButton,
  Timer,
  TreeView,
} from '@phosphor-icons/react';
import _ from 'lodash';
import { Block, BlockType } from '../../../../types/Study';
import { convertSecondsToDisplayTime } from '../../../../utils/convertSecondsToDisplayTime';
import { capture } from '../../../../utils/analytics';
import moment from 'moment';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getRefName, resolveBlockTitle } from './resolveBlockTitle';

interface PastQBankBlockProps {
  title?: string | undefined;
  block: Block;
  reviewBlockFunction: () => void;
  /**
   * Overrides the default per-state action label ("Continue Block" /
   * "Review Block"). Used for the faculty read-only context ("View Block").
   */
  actionLabelOverride?: string;
  /**
   * Analytics event fired on click. Pass `null` to suppress (e.g. admin
   * context). Defaults to the student review event.
   */
  analyticsEventName?: string | null;
  /**
   * Opens a source block by id (review if submitted, continue otherwise).
   * Enables the variant-retry source-block badges; omit to hide that row.
   */
  onOpenSourceBlock?: (blockId: string) => void;
  /**
   * When provided, renders a rename control on the card that calls this with
   * the block. Omit in read-only contexts (faculty panel) to hide it.
   */
  onRename?: (block: Block) => void;
}

const PastQBankBlock: React.FC<PastQBankBlockProps> = ({
  title,
  block,
  reviewBlockFunction,
  actionLabelOverride,
  analyticsEventName = 'custom_qbank_block_review',
  onOpenSourceBlock,
  onRename,
}) => {
  const isReviewAvailable = block.isReviewAvailable !== false;

  // Source blocks a variant-retry block was created from. Prefer the resolved
  // state from the API; fall back to ids (treated as submitted -> review) for
  // resilience if a client hits an older response shape.
  const variantSourceBlocks =
    block.sourceBlocks && block.sourceBlocks.length > 0
      ? block.sourceBlocks
      : Array.isArray(block.sourceBlockIds)
        ? block.sourceBlockIds.map((id) => ({ id, isSubmitted: true }))
        : [];

  const actionLabel =
    actionLabelOverride ??
    (block.isSubmitted === false ? 'Continue Block' : 'Review Block');

  const isTutorInProgress =
    block.isSubmitted === false && block.type === BlockType.tutor;

  const handleReviewBlock = () => {
    hapticsImpactLight();
    if (analyticsEventName) {
      capture(analyticsEventName, { blockId: block.id });
    }
    reviewBlockFunction();
  };

  const resolvedAnsweredCount = isTutorInProgress
    ? block.correctCount + block.incorrectCount
    : typeof block.answerCount === 'number'
      ? block.answerCount
      : block.correctCount + block.incorrectCount;

  const resolvedTimeRemainingSeconds =
    typeof block.timeRemainingSeconds === 'number'
      ? block.timeRemainingSeconds
      : block.duration;

  const renderExamNames = () => {
    const resolvedExams = block.filterSet?.exams ?? [];

    const examNames = resolvedExams
      .map(getRefName)
      .filter((name): name is string => Boolean(name));

    if (examNames.length > 0) {
      if (examNames.length === 1) {
        return <span className="eyebrow">{examNames[0]}</span>;
      } else {
        return (
          <span className="eyebrow">
            {examNames[0]}
            <Tooltip
              title={examNames.slice(1).join(', ')}
              enterTouchDelay={0}
              enterDelay={250}
            >
              <span className="eyebrow_more">+{examNames.length - 1}</span>
            </Tooltip>
          </span>
        );
      }
    }
    return null;
  };

  const renderTitle = (title: string | undefined) => {
    const resolvedTitle = resolveBlockTitle(block, title);
    return (
      <div className="flex w-full min-w-0 items-center gap-1.5">
        <h3 className="min-w-0" title={resolvedTitle.tooltip ?? undefined}>
          {resolvedTitle.text}
        </h3>
        {onRename && (
          <Tooltip title="Rename Block" enterTouchDelay={0} enterDelay={250}>
            <button
              type="button"
              aria-label="Rename Block"
              className="-my-1 flex flex-shrink-0 items-center justify-center rounded p-1 text-fg-secondary transition pointer-fine:opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 hover:text-fg-primary"
              onClick={(event) => {
                event.stopPropagation();
                onRename(block);
              }}
            >
              <PencilSimple size={16} />
            </button>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderTopicNames = () => {
    if (
      block?.filterSet?.filterType === 'topic' ||
      block?.filterSet?.filterType === 'session'
    ) {
      return <></>;
    }
    if (block?.filterSet?.filterType === 'custom') {
      if (block.filterSet?.topics && block.filterSet?.topics.length > 0) {
        const topicNames = block.filterSet.topics
          .map(getRefName)
          .filter((name): name is string => Boolean(name));

        return (
          <div
            className="past-qbank-block_body_detail"
            style={{ transform: 'translateY(-2px)' }}
          >
            <TreeView />
            {topicNames[0]}
            {topicNames.length > 1 && (
              <Tooltip
                title={topicNames.slice(1).join(', ')}
                enterTouchDelay={0}
                enterDelay={250}
              >
                <Badge variant="secondary">+{topicNames.length - 1}</Badge>
              </Tooltip>
            )}
          </div>
        );
      } else {
        return (
          <div className="past-qbank-block_body_detail">
            <TreeView />
            All Topics
          </div>
        );
      }
    }
    return <></>;
  };

  return (
    <div
      className="past-qbank-block group"
      role={isReviewAvailable ? 'button' : undefined}
      onClick={isReviewAvailable ? handleReviewBlock : undefined}
      tabIndex={isReviewAvailable ? 0 : -1}
      aria-label={isReviewAvailable ? actionLabel : undefined}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;

        if (isReviewAvailable && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          handleReviewBlock();
        }
      }}
    >
      <div className="past-qbank-block_body">
        <div className="left">
          {renderExamNames()}
          {renderTitle(title)}
          {!_.isNil(block.isSubmitted) && (
            <div className="past-qbank-block_body_detail">
              <Calendar />
              {moment(block.date).format('MMMM D, YYYY')}
            </div>
          )}
          <div className="past-qbank-block_body_detail">
            <RadioButton />
            {Boolean(!_.isNil(block.isSubmitted) && !block.isSubmitted) && (
              <>{resolvedAnsweredCount} of </>
            )}
            {block.vignetteCount} Question
            {block.vignetteCount === 1 ? '' : 's'}
            {Boolean(!_.isNil(block.isSubmitted) && !block.isSubmitted) && (
              <> Answered</>
            )}
            {block.filterSet?.filters?.attribute &&
              block.filterSet?.filters?.attribute !== 'all' && (
                <>
                  {' '}
                  |{' '}
                  {block.filterSet?.filters?.attribute.charAt(0).toUpperCase() +
                    block.filterSet?.filters?.attribute.slice(1)}
                </>
              )}
          </div>
          <div className="past-qbank-block_body_detail">
            {block.type === BlockType.timed ? <Timer /> : <BookOpen />}
            {block.type === BlockType.timed ? 'Timed' : 'Untimed'} Mode
            {!block.isSubmitted && block.type === BlockType.timed && (
              <>
                {' '}
                |{' '}
                {convertSecondsToDisplayTime(
                  Math.max(0, resolvedTimeRemainingSeconds ?? 0)
                )}{' '}
                Remaining
              </>
            )}
          </div>
          {renderTopicNames()}
          {block?.isVariantRetryBlock &&
            onOpenSourceBlock &&
            variantSourceBlocks.length > 0 && (
              <div className="past-qbank-block_body_detail">
                <GitMerge />
                <div className="flex flex-wrap items-center gap-1">
                  {variantSourceBlocks.length > 1 ? (
                    <>
                      {variantSourceBlocks.map((sourceBlock, index) => {
                        const sourceActionLabel = sourceBlock.isSubmitted
                          ? 'Review Block'
                          : 'Continue Block';

                        return (
                          <Tooltip
                            key={sourceBlock.id}
                            title={sourceActionLabel}
                            enterTouchDelay={0}
                            enterDelay={250}
                          >
                            <button
                              type="button"
                              className="inline-flex cursor-pointer appearance-none border-0 bg-transparent p-0"
                              aria-label={sourceActionLabel}
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenSourceBlock(sourceBlock.id);
                              }}
                            >
                              <Badge variant="secondary">{index + 1}</Badge>
                            </button>
                          </Tooltip>
                        );
                      })}
                    </>
                  ) : (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      style={{ minHeight: 'auto', height: 'auto' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenSourceBlock(variantSourceBlocks[0].id);
                      }}
                    >
                      {variantSourceBlocks[0].isSubmitted
                        ? 'Review'
                        : 'Continue'}{' '}
                      Source Block
                    </Button>
                  )}
                </div>
              </div>
            )}
        </div>
        {block.vignetteCount !== block.omittedCount &&
          block.correctCount + block.incorrectCount !== 0 &&
          block.filterSet?.filters?.attribute !== 'incorrect' && (
            <Tooltip
              title={`${block.correctCount} Correct, ${block.incorrectCount} Incorrect, ${block.omittedCount} Omitted`}
              enterTouchDelay={0}
              enterDelay={250}
            >
              <div className="right">
                <div className="percentage-display">
                  <span className="percentage-display_value">
                    {_.isNil(block.correctCount) || block.correctCount === 0
                      ? 0
                      : Math.round(
                          (block.correctCount /
                            (block.correctCount +
                              block.incorrectCount +
                              block.omittedCount)) *
                            100
                        )}
                  </span>
                  <span className="percentage-display_suffix">%</span>
                </div>
                <div className="distribution-display">
                  <div
                    className="distribution-display_item distribution-display_item--correct"
                    style={{
                      width: `${(block.correctCount / block.vignetteCount) * 100}%`,
                    }}
                  ></div>
                  <div
                    className="distribution-display_item distribution-display_item--incorrect"
                    style={{
                      width: `${(block.incorrectCount / block.vignetteCount) * 100}%`,
                      left: `${(block.correctCount / block.vignetteCount) * 100}%`,
                    }}
                  ></div>
                  <div
                    className="distribution-display_item distribution-display_item--omitted"
                    style={{
                      width: `${(block.omittedCount / block.vignetteCount) * 100}%`,
                      left: `calc(${(block.correctCount / block.vignetteCount) * 100}% + ${(block.incorrectCount / block.vignetteCount) * 100}%)`,
                    }}
                  ></div>
                </div>
              </div>
            </Tooltip>
          )}
      </div>
      {isReviewAvailable && (
        <div className="button button--link">
          {actionLabel}
          <ArrowRight />
        </div>
      )}
    </div>
  );
};

export default PastQBankBlock;
